import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Modal, Badge, Form, InputGroup, Card } from "react-bootstrap";
import { 
  MegaphoneFill, 
  PlusLg, 
  Search, 
  CalendarCheck, 
  Trash, 
  Save, 
  ArrowLeft,
  Paperclip,
  CheckCircleFill,
  ClockFill
} from "react-bootstrap-icons";
import { toast } from "react-toastify";
import CustomModal from "../Modals/CustomModal.jsx";
import {
  addOutboundNotice,
  deleteOutboundNotice,
  getOutboundNoticeManageList,
  updateOutboundNotice,
  uploadOutboundNotifyImage,
} from "../../models/Query";
import "../../quillSetup";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useAuth } from "../../context/AuthContext";

const initialForm = {
  seq: null,
  notice_date: "",
  publish_start_date: "",
  publish_end_date: "",
  title: "",
  content: "",
  attachment_file: "",
};

const formatDateTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

const formatDateInput = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const OutboundNoticeManagement = () => {
  const navigate = useNavigate();
  const { Server_IP, token, userType } = useAuth();
  const [notices, setNotices] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [selectedSeq, setSelectedSeq] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [applyModalMessage, setApplyModalMessage] = useState("");
  const [searchFilter, setSearchFilter] = useState("");

  const isEditMode = useMemo(() => Boolean(selectedSeq), [selectedSeq]);
  const quillRef = useRef(null);
  const [isClient, setIsClient] = useState(false);
  const imageHandler = useCallback(() => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();
    input.onchange = async () => {
      const file = input.files[0];
      if (!file) return;
      const resp = await uploadOutboundNotifyImage(Server_IP, token, file);
      if (resp && resp.url) {
        const quill = quillRef.current && quillRef.current.getEditor ? quillRef.current.getEditor() : null;
        if (quill) {
          const range = quill.getSelection(true);
          quill.insertEmbed(range ? range.index : 0, "image", resp.url);
        } else {
          setForm((prev) => ({ ...prev, content: prev.content + `<img src="${resp.url}" alt="image"/>` }));
        }
      } else {
        toast.error("이미지 업로드에 실패했습니다.");
      }
    };
  }, [Server_IP, token]);

  const quillModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ align: [] }],
        ["link", "image"],
        ["clean"],
      ],
      handlers: {
        image: imageHandler,
      },
    },
  }), [imageHandler]);

  const refreshList = async () => {
    const list = await getOutboundNoticeManageList(Server_IP, token);
    setNotices(list);
  };

  useEffect(() => {
    if (userType !== "manager") {
      toast.error("관리자만 접근할 수 있습니다.");
      navigate("/");
      return;
    }

    refreshList();
  }, [Server_IP, token, userType, navigate]);

  // Ensure editor only renders on client
  useEffect(() => {
    setIsClient(true);
  }, []);

  const handleSelect = (notice) => {
    setSelectedSeq(notice.seq);
    setForm({
      seq: notice.seq,
      notice_date: formatDateTime(notice.notice_date),
      publish_start_date: formatDateInput(notice.publish_start_date),
      publish_end_date: formatDateInput(notice.publish_end_date),
      title: notice.title || "",
      content: notice.content || "",
      attachment_file: notice.attachment_file || "",
    });
  };

  const handleNew = () => {
    setSelectedSeq(null);
    setForm({
      ...initialForm,
      notice_date: formatDateTime(new Date()),
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();

    if (!form.publish_start_date || !form.publish_end_date || !form.title.trim() || !form.content.trim()) {
      toast.error("게시기간, 제목, 내용은 필수입니다.");
      return;
    }

    const normalizeAlignment = (html) => {
      if (!html) return html;
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        doc.body.querySelectorAll("[style]").forEach((el) => {
          const s = el.getAttribute("style") || "";
          const m = s.match(/text-align\s*:\s*(left|center|right)\s*;?/i);
          if (m) {
            const align = m[1].toLowerCase();
            // add class
            const prevClass = el.getAttribute("class") || "";
            const classes = prevClass.split(/\s+/).filter(Boolean);
            if (!classes.includes(`ql-align-${align}`)) classes.push(`ql-align-${align}`);
            el.setAttribute("class", classes.join(" ").trim());
            // remove only text-align from style
            const newStyle = s.replace(/text-align\s*:\s*(left|center|right)\s*;?/ig, "").trim();
            if (newStyle) el.setAttribute("style", newStyle);
            else el.removeAttribute("style");
          }
        });
        return doc.body.innerHTML;
      } catch (err) {
        return html;
      }
    };

    const payload = {
      ...form,
      notice_date: form.notice_date || formatDateTime(new Date()),
      content: normalizeAlignment(form.content || ""),
    };

    const result = isEditMode
      ? await updateOutboundNotice(Server_IP, token, payload)
      : await addOutboundNotice(Server_IP, token, payload);

    if (result) {
      setApplyModalMessage(
        isEditMode
          ? "공지사항이 수정되었습니다.\n반영 완료되었습니다."
          : "공지사항이 등록되었습니다.\n반영 완료되었습니다.",
      );
      setShowApplyModal(true);
      if (!isEditMode) handleNew();
      await refreshList();
    } else {
      toast.error(isEditMode ? "수정에 실패했습니다." : "등록에 실패했습니다.");
    }
  };

  const handleDelete = async () => {
    if (!selectedSeq) return;
    const result = await deleteOutboundNotice(Server_IP, token, selectedSeq);
    if (result) {
      toast.success("공지사항이 삭제되었습니다.");
      setShowDeleteModal(false);
      handleNew();
      await refreshList();
    } else {
      toast.error("삭제에 실패했습니다.");
    }
  };

  const handleClose = () => {
    window.close();
  };

  const filteredNotices = useMemo(() => {
    const query = searchFilter.toLowerCase().trim();
    if (!query) return notices;
    return notices.filter(n => 
      (n.title?.toLowerCase().includes(query)) ||
      (n.content?.toLowerCase().includes(query))
    );
  }, [notices, searchFilter]);

  const getNoticeStatus = (start, end) => {
    const now = new Date();
    // Use midnight for comparison
    now.setHours(0, 0, 0, 0);
    const startDate = new Date(start);
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(end);
    endDate.setHours(0, 0, 0, 0);

    if (now < startDate) {
      return {
        label: "대기",
        icon: <ClockFill size={11} className="me-1" />,
        badgeStyle: {
          backgroundColor: "#ea8c1a",
          border: "1px solid #c86e08",
          color: "#fff",
          boxShadow: "0 8px 18px rgba(234, 140, 26, 0.28)",
        },
      };
    } else if (now >= startDate && now <= endDate) {
      return {
        label: "진행",
        icon: <CheckCircleFill size={11} className="me-1" />,
        badgeStyle: {
          backgroundColor: "#2563eb",
          border: "1px solid #1d4ed8",
          color: "#fff",
          boxShadow: "0 8px 18px rgba(37, 99, 235, 0.28)",
        },
      };
    } else {
      return {
        label: "종료",
        icon: null,
        badgeStyle: {
          backgroundColor: "#475569",
          border: "1px solid #334155",
          color: "#fff",
          boxShadow: "0 8px 18px rgba(71, 85, 105, 0.22)",
        },
      };
    }
  };

  return (
    <div className="p-4 bg-light min-vh-100">
      <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
        <div>
          <h2 className="mb-1 fw-bold d-flex align-items-center gap-2">
            <MegaphoneFill className="text-primary" />
            외부 공지사항 관리
          </h2>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-dark" className="d-flex align-items-center gap-2 px-3 shadow-sm" onClick={handleClose}>
            <ArrowLeft /> 닫기
          </Button>
          <Button variant="primary" className="d-flex align-items-center gap-2 px-3 shadow-sm" onClick={handleNew}>
            <PlusLg /> 초기화
          </Button>
        </div>
      </div>

      <div className="row g-4">
        <div className="col-12 col-xl-7">
          <Card className="border-0 shadow-sm overflow-hidden h-100">
            <Card.Header className="py-3 border-0 text-white fw-bold bg-primary d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-2">
                <MegaphoneFill className="text-white" />
                <span>공지 목록 ({filteredNotices.length})</span>
              </div>
              <div className="notice-search-box">
                <span className="notice-search-icon">
                  <Search size={15} />
                </span>
                <Form.Control
                  placeholder="제목/내용 검색"
                  className="notice-search-input"
                  value={searchFilter}
                  onChange={(e) => setSearchFilter(e.target.value)}
                />
              </div>
            </Card.Header>
            <div className="table-responsive" style={{ maxHeight: "calc(100vh - 250px)" }}>
              <table className="table table-hover mb-0 align-middle customize-table">
                <thead className="table-light sticky-top shadow-none border-bottom">
                  <tr>
                    <th className="px-4 py-3" style={{ width: 60 }}>#</th>
                    <th style={{ width: 100 }}>상태</th>
                    <th style={{ width: 180 }}>게시 기간</th>
                    <th>공지 제목</th>
                    <th style={{ width: 80 }}>첨부</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredNotices.length > 0 ? filteredNotices.map((notice) => {
                    const isSelected = selectedSeq === notice.seq;
                    const status = getNoticeStatus(notice.publish_start_date, notice.publish_end_date);
                    return (
                      <tr
                        key={notice.seq}
                        className={isSelected ? "table-primary" : ""}
                        role="button"
                        onClick={() => handleSelect(notice)}
                      >
                        <td className="px-4 font-monospace text-muted small">{notice.seq}</td>
                        <td>
                          <Badge
                            pill
                            className="d-inline-flex align-items-center justify-content-center fw-semibold"
                            style={{
                              ...status.badgeStyle,
                              minWidth: "78px",
                              padding: "0.5rem 0.85rem",
                              fontSize: "0.8rem",
                              letterSpacing: "0.02em",
                            }}
                          >
                            {status.icon}
                            {status.label}
                          </Badge>
                        </td>
                        <td>
                          <div className="d-flex flex-column small">
                            <span className="text-dark fw-bold">{formatDateInput(notice.publish_start_date)}</span>
                            <span className="text-muted">{formatDateInput(notice.publish_end_date)}</span>
                          </div>
                        </td>
                        <td>
                          <div className="fw-bold text-truncate" style={{ maxWidth: 300 }}>
                            {notice.title}
                          </div>
                          <div className="text-muted small text-truncate" style={{ maxWidth: 300 }}>
                            {notice.content}
                          </div>
                        </td>
                        <td>
                          {notice.attachment_file ? <Paperclip className="text-primary" /> : <span className="text-light">-</span>}
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr>
                      <td colSpan="5" className="text-center py-5 text-muted">
                        공지사항이 없습니다.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        <div className="col-12 col-xl-5">
          <Card className="border-0 shadow-sm sticky-top h-100" style={{ top: "20px" }}>
            <Card.Header className={`py-3 border-0 text-white fw-bold ${isEditMode ? "bg-info" : "bg-primary"}`}>
              <div className="d-flex align-items-center gap-2">
                <PlusLg />
                {isEditMode ? `공지사항 수정 (No.${selectedSeq})` : "신규 공지사항 등록"}
              </div>
            </Card.Header>
            <Card.Body className="p-4">
              <Form onSubmit={handleSave}>
                <div className="row g-3">
                  <div className="col-12">
                    <Form.Group>
                      <Form.Label className="small fw-bold text-muted text-uppercase mb-1">작성일시</Form.Label>
                      <Form.Control
                        className="bg-light border-0"
                        value={form.notice_date}
                        readOnly
                        size="sm"
                      />
                    </Form.Group>
                  </div>
                  <div className="col-6">
                    <Form.Group>
                      <Form.Label className="small fw-bold text-muted text-uppercase mb-1">게시 시작일</Form.Label>
                      <Form.Control
                        type="date"
                        value={form.publish_start_date}
                        onChange={handleChange}
                        name="publish_start_date"
                        className="border-0 bg-light-soft px-3 py-2"
                      />
                    </Form.Group>
                  </div>
                  <div className="col-6">
                    <Form.Group>
                      <Form.Label className="small fw-bold text-muted text-uppercase mb-1">게시 마감일</Form.Label>
                      <Form.Control
                        type="date"
                        value={form.publish_end_date}
                        onChange={handleChange}
                        name="publish_end_date"
                        className="border-0 bg-light-soft px-3 py-2"
                      />
                    </Form.Group>
                  </div>
                  <div className="col-12">
                    <Form.Group>
                      <Form.Label className="small fw-bold text-muted text-uppercase mb-1">공지 제목</Form.Label>
                      <Form.Control
                        placeholder="제목을 입력하세요"
                        value={form.title}
                        onChange={handleChange}
                        name="title"
                        className="border-0 bg-light-soft px-3 py-2"
                        style={{ fontSize: "1.1rem", fontWeight: "bold" }}
                      />
                    </Form.Group>
                  </div>
                  <div className="col-12">
                    <Form.Group>
                      <Form.Label className="small fw-bold text-muted text-uppercase mb-1">상세 내용</Form.Label>
                      <div className="border-0 bg-light-soft p-0">
                        {isClient ? (
                          <ReactQuill
                            theme="snow"
                            value={form.content || ""}
                            onChange={(value) => setForm((prev) => ({ ...prev, content: value }))}
                            ref={quillRef}
                            formats={["header", "bold", "italic", "underline", "strike", "list", "bullet", "link", "image", "align"]}
                            modules={quillModules}
                            style={{ minHeight: 240 }}
                          />
                        ) : (
                          <div style={{ minHeight: 240 }} />
                        )}
                      </div>
                    </Form.Group>
                  </div>
                  <div className="col-12">
                    <Form.Group>
                      <Form.Label className="small fw-bold text-muted text-uppercase mb-1">첨부파일 경로</Form.Label>
                      <InputGroup className="bg-light-soft">
                        <InputGroup.Text className="bg-transparent border-0 pe-0">
                          <Paperclip />
                        </InputGroup.Text>
                        <Form.Control
                          className="border-0 bg-transparent py-2"
                          value={form.attachment_file}
                          onChange={handleChange}
                          name="attachment_file"
                          placeholder="배포 URL 또는 공유 폴더 경로"
                        />
                      </InputGroup>
                    </Form.Group>
                  </div>
                </div>

                <div className="d-flex gap-3 mt-4 pt-3 border-top">
                  {isEditMode && (
                    <Button
                      type="button"
                      variant="outline-danger"
                      className="px-4 d-flex align-items-center gap-2"
                      onClick={() => setShowDeleteModal(true)}
                    >
                      <Trash /> 삭제
                    </Button>
                  )}
                  <Button 
                    type="submit"
                    variant={isEditMode ? "info" : "primary"} 
                    className="flex-fill text-white fw-bold d-flex align-items-center justify-content-center gap-2 py-2 shadow-sm"
                  >
                    <Save /> 
                    {isEditMode ? "저장" : "공지사항 등록"}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </div>
        </div>

      <CustomModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        width={420}
        height={160}
        message="선택한 공지사항을 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다."
        confirmText="삭제하기"
        cancelText="닫기"
      />

      <CustomModal
        show={showApplyModal}
        onHide={() => setShowApplyModal(false)}
        onConfirm={() => setShowApplyModal(false)}
        width={420}
        height={160}
        message={applyModalMessage}
        confirmText="확인"
        cancelText=""
      />
      
      <style>{`
        .bg-light-soft {
          background-color: #f1f5f9;
        }
        .bg-light-soft:focus {
          background-color: #fff;
          box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
        }
        .notice-search-box {
          width: 250px;
          display: flex;
          align-items: stretch;
          overflow: hidden;
          border: 1px solid rgba(255, 255, 255, 0.28);
          border-radius: 999px;
          background: #fff;
        }
        .notice-search-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 0.85rem;
          color: #64748b;
          background: transparent;
          flex: 0 0 auto;
        }
        .notice-search-input {
          border: 0;
          border-radius: 0;
          box-shadow: none !important;
          background: transparent;
          min-height: 34px;
          padding: 0.28rem 0.95rem 0.28rem 0;
        }
        .notice-search-input:focus {
          box-shadow: none !important;
          background: transparent;
        }
        .table-primary {
          background-color: #eff6ff !important;
          border-left: 3px solid #3b82f6 !important;
        }
      `}</style>
    </div>
  );
};

export default OutboundNoticeManagement;